import React, { useEffect, useState } from 'react';
import { KeyGoal } from '../../types';

interface KeyGoalModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: (keyGoal: KeyGoal) => Promise<void | { needsConfirmation: true }>;
    onDelete?: (keyGoal: KeyGoal) => void;
    keyGoal: KeyGoal | null;
}

const KeyGoalModal: React.FC<KeyGoalModalProps> = ({
    isOpen,
    onClose,
    onSave,
    onDelete,
    keyGoal
}) => {
    const [goal, setGoal] = useState<string>('');
    const [definition, setDefinition] = useState<string>('');
    const [formErrors, setFormErrors] = useState<{ [key: string]: string }>({});
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [charCount, setCharCount] = useState<number>(0);
    const maxChars = 500;

    // Reset form when modal is closed or opened
    useEffect(() => {
        if (isOpen) {
            if (keyGoal) {
                setGoal(keyGoal.goal);
                setDefinition(keyGoal.definition);
                setCharCount(keyGoal.definition.length);
            } else {
                resetForm();
            }
        }
    }, [isOpen, keyGoal]);

    // Function to reset form fields to initial values
    const resetForm = () => {
        setGoal('');
        setDefinition('');
        setFormErrors({});
        setCharCount(0);
    };

    // Handle escape key press
    useEffect(() => {
        if (!isOpen) return;

        const handleEsc = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                onClose();
            }
        };

        window.addEventListener('keydown', handleEsc);
        return () => window.removeEventListener('keydown', handleEsc);
    }, [isOpen, onClose]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;

        // Update the corresponding state based on the input name
        if (name === 'goal') {
            setGoal(value);
        } else if (name === 'definition') {
            setDefinition(value);
            setCharCount(value.length);
        }

        // Clear error when field is edited
        if (formErrors[name]) {
            setFormErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const validateForm = (): boolean => {
        const errors: { [key: string]: string } = {};

        if (!goal?.trim()) {
            errors.goal = 'Goal name is required';
        }

        if (!definition?.trim()) {
            errors.definition = 'Goal description is required';
        }

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (validateForm() && !isSubmitting) {
            setIsSubmitting(true);
            try {
                const result = await onSave({
                    id: keyGoal?.id,
                    goal,
                    definition
                });
                if (result?.needsConfirmation) {
                    setIsSubmitting(false);
                    return;
                }
                resetForm();
            } finally {
                setIsSubmitting(false);
            }
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-end z-50">
            <div className="bg-white w-full max-w-md h-full overflow-y-auto shadow-xl">
                <div className="p-6">
                    <div className="flex justify-between items-center mb-6">
                        <h2 className="text-xl font-bold text-[#0F2A4F]">
                            {keyGoal ? 'Edit Key Goal' : 'Add Key Goal'}
                        </h2>
                        <button
                            onClick={onClose}
                            className="rounded-full p-2 hover:bg-gray-100"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>

                    <form onSubmit={handleSubmit}>
                        <div className="mb-4">
                            <input
                                type="text"
                                name="goal"
                                placeholder="Goal name"
                                value={goal}
                                onChange={handleChange}
                                className={`w-full px-3 py-2 border ${formErrors.goal ? 'border-red-500' : 'border-gray-300'} rounded-md`}
                            />
                            {formErrors.goal && (
                                <p className="mt-1 text-sm text-red-500">{formErrors.goal}</p>
                            )}
                        </div>

                        <div className="mb-4">
                            <textarea
                                name="definition"
                                placeholder="Goal description"
                                value={definition}
                                onChange={handleChange}
                                className={`w-full px-3 py-2 border ${formErrors.definition ? 'border-red-500' : 'border-gray-300'} rounded-md h-32`}
                                maxLength={maxChars}
                            />
                            <div className="text-xs text-gray-500 mt-1">
                                {charCount}/{maxChars} chars remaining
                            </div>
                            {formErrors.definition && (
                                <p className="mt-1 text-sm text-red-500">{formErrors.definition}</p>
                            )}
                        </div>

                        <div className={`mt-6 flex justify-end ${keyGoal && onDelete && 'justify-between'}`}>
                            {/* Delete button - only show for existing goals */}
                            {keyGoal && onDelete && (
                                <button
                                    type="button"
                                    onClick={() => {
                                        if (keyGoal && onDelete) {
                                            onDelete(keyGoal);
                                            onClose();
                                        }
                                    }}
                                    disabled={isSubmitting}
                                    className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
                                >
                                    Delete
                                </button>
                            )}

                            <div className="flex">
                                <button
                                    type="button"
                                    onClick={onClose}
                                    disabled={isSubmitting}
                                    className={`px-4 py-2 border border-gray-300 rounded-md mr-2 ${isSubmitting ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-50'}`}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={isSubmitting}
                                    className={`px-4 py-2 bg-[#0F2A4F] text-white rounded-md flex items-center ${isSubmitting ? 'opacity-70 cursor-not-allowed' : 'hover:bg-[#204374]'}`}
                                >
                                    {isSubmitting ? (
                                        <>
                                            <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
                                            {keyGoal ? 'Updating...' : 'Creating...'}
                                        </>
                                    ) : (
                                        keyGoal ? 'Confirm' : 'Add Goal'
                                    )}
                                </button>
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default KeyGoalModal;
